<template>
  <div class="additional-sections">
    <div class="additional-sections__head">
      <div class="additional-sections__intro">
        <div class="additional-sections__title">{{ $t("resources.pages.additional_sections_title") }}</div>
        <div class="additional-sections__subtitle">{{ $t("resources.pages.additional_sections_subtitle") }}</div>
      </div>
      <div class="additional-sections__head-actions">
        <v-btn
          outlined
          color="primary"
          class="additional-sections__head-btn"
          :disabled="!sections.length"
          @click="$emit('replace')">
          {{ $t("resources.pages.additional_sections_replace_content") }}
        </v-btn>
        <v-btn
          outlined
          color="primary"
          class="additional-sections__head-btn"
          :disabled="!sections.length"
          @click="allExpanded ? collapseAll() : expandAll()">
          {{
            allExpanded
              ? $t("resources.pages.additional_sections_collapse")
              : $t("resources.pages.additional_sections_expand")
          }}
        </v-btn>
        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <v-icon
              size="24"
              class="additional-sections__head-action"
              :color="sections.length ? 'error' : null"
              v-bind="attrs"
              v-on="on"
              @click="sections.length ? $emit('delete-all') : $emit('close')"
              >{{ sections.length ? "mdi-trash-can-outline" : "mdi-close" }}</v-icon
            >
          </template>
          {{
            sections.length
              ? $t("resources.pages.additional_sections_delete_all")
              : $t("resources.pages.additional_sections_close")
          }}
        </v-tooltip>
      </div>
    </div>

    <draggable
      v-if="sections.length"
      v-model="sections"
      handle=".additional-sections__handle"
      ghost-class="additional-sections__row--ghost"
      :animation="150"
      @choose="collapseDragged"
      @start="dragging = true"
      @end="onDragEnd">
      <div v-for="section in sections" :key="section._key" class="additional-sections__row">
        <div class="additional-sections__bar">
          <div class="additional-sections__lead">
            <v-icon class="additional-sections__handle" size="24">mdi-drag-vertical</v-icon>

            <div
              class="additional-sections__box"
              :class="{ 'additional-sections__box--disabled': !section.enabled }"
              @click="toggleExpand(section)">
              <div class="additional-sections__box-main">
                <span
                  class="additional-sections__badge"
                  :class="{ 'additional-sections__badge--disabled': !section.enabled }">
                  {{ enabledNumber(section) || "—" }}
                </span>
                <span class="additional-sections__divider"></span>
                <div class="additional-sections__title-wrap">
                  <template v-if="editingKey === section._key">
                    <v-text-field
                      v-model="editingTitle"
                      dense
                      hide-details
                      autofocus
                      class="additional-sections__title-input"
                      @click.native.stop
                      @mousedown.native.stop
                      @keydown.enter.prevent="commitTitle(section)"
                      @keydown.esc.prevent="cancelTitle"
                      @blur="commitTitle(section)"></v-text-field>
                  </template>
                  <template v-else>
                    <span class="additional-sections__row-title">{{ displayTitle(section) }}</span>
                    <v-tooltip top>
                      <template #activator="{ on, attrs }">
                        <v-icon
                          size="16"
                          class="additional-sections__title-edit"
                          v-bind="attrs"
                          v-on="on"
                          @click.stop="startEditTitle(section)"
                          >mdi-pencil-outline</v-icon
                        >
                      </template>
                      {{ $t("resources.pages.additional_sections_edit_title") }}
                    </v-tooltip>
                  </template>
                </div>
              </div>

              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    size="24"
                    class="additional-sections__chevron"
                    :class="{ 'additional-sections__chevron--open': isExpanded(section) }"
                    v-bind="attrs"
                    v-on="on"
                    >mdi-chevron-down</v-icon
                  >
                </template>
                {{ $t("resources.pages.additional_sections_expand_collapse") }}
              </v-tooltip>
            </div>
          </div>

          <div class="additional-sections__trail">
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <div class="additional-sections__toggle" v-bind="attrs" v-on="on">
                  <v-switch
                    v-model="section.enabled"
                    inset
                    dense
                    hide-details
                    color="primary"
                    :ripple="false"
                    class="additional-sections__switch"></v-switch>
                </div>
              </template>
              {{ $t("resources.pages.additional_sections_show_on_page") }}
            </v-tooltip>
            <span class="additional-sections__divider"></span>
            <v-tooltip v-if="rtbSorting" top max-width="280">
              <template #activator="{ on, attrs }">
                <v-icon
                  size="24"
                  class="additional-sections__lock"
                  :color="section.rtbLocked ? 'primary' : null"
                  v-bind="attrs"
                  v-on="on"
                  @click="section.rtbLocked = !section.rtbLocked"
                  >{{ section.rtbLocked ? "mdi-lock" : "mdi-lock-open-variant-outline" }}</v-icon
                >
              </template>
              {{ $t("resources.pages.additional_sections_rtb_lock_tooltip") }}
            </v-tooltip>
            <span v-if="rtbSorting" class="additional-sections__divider"></span>
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-icon
                  size="24"
                  class="additional-sections__trash"
                  v-bind="attrs"
                  v-on="on"
                  @click="requestDelete(section)"
                  >mdi-trash-can-outline</v-icon
                >
              </template>
              {{ $t("resources.pages.additional_sections_delete") }}
            </v-tooltip>
          </div>
        </div>

        <v-expand-transition>
          <div
            v-show="isExpanded(section)"
            class="additional-sections__editor"
            :class="{ 'additional-sections__editor--dragging': dragging }"
            :style="editorReservation(section)">
            <va-tinymce-input
              v-if="mountedKeys.includes(section._key)"
              :key="`editor-${section._key}-${editorRefreshKey}`"
              :current-content="section.content"
              :editor-height="editorHeight"
              :additional-sections="true"
              :image-upload-queue.sync="queue"
              @update="onContentUpdate(section, $event)" />
          </div>
        </v-expand-transition>
      </div>
    </draggable>

    <button type="button" class="additional-sections__add" @click="addSection">
      <v-icon size="16" color="primary" class="mr-1">mdi-plus</v-icon>
      {{ $t("resources.pages.additional_sections_add") }}
    </button>

    <Modal
      width="400"
      :value="!!deleteTargetKey"
      :title="$t('resources.pages.additional_sections_delete_modal_title')"
      :subtitle="$t('resources.pages.additional_sections_delete_modal_subtitle')"
      :cta-text="$t('generic.buttons.delete')"
      :cta-color="'error'"
      :dialog-icon="'danger'"
      @confirm="confirmDelete"
      @cancel="cancelDelete">
    </Modal>
  </div>
</template>

<script>
  import draggable from "vuedraggable";
  import Modal from "@/components/custom/Modal";
  import { SECTION_DEFAULT_TITLE } from "@/constant";

  // Matches the Vuetify expand transition duration.
  const EXPAND_TRANSITION_MS = 300;

  export default {
    name: "AdditionalSectionsPanel",
    components: {
      draggable,
      Modal,
    },
    props: {
      value: {
        type: Array,
        default: () => [],
      },
      imageUploadQueue: {
        type: Array,
        default: () => [],
      },
      rtbSorting: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        expandedKeys: [],
        mountedKeys: [],
        editingKey: null,
        editingTitle: "",
        deleteTargetKey: null,
        localCounter: 0,
        editorRefreshKey: 0,
        dragging: false,
        editorHeight: 500,
      };
    },
    computed: {
      sections: {
        get() {
          return this.value;
        },
        set(val) {
          this.$emit("input", val);
        },
      },
      queue: {
        get() {
          return this.imageUploadQueue;
        },
        set(val) {
          this.$emit("update:imageUploadQueue", val);
        },
      },
      allExpanded() {
        return this.sections.length > 0 && this.sections.every(section => this.isExpanded(section));
      },
    },
    methods: {
      displayTitle(section) {
        return section.title || SECTION_DEFAULT_TITLE;
      },
      enabledNumber(section) {
        if (!section.enabled) return null;

        let number = 0;
        for (const item of this.sections) {
          if (item.enabled) number += 1;
          if (item._key === section._key) return number;
        }
        return null;
      },
      isExpanded(section) {
        return this.expandedKeys.includes(section._key);
      },
      toggleExpand(section) {
        const key = section._key;
        if (this.isExpanded(section)) {
          this.expandedKeys = this.expandedKeys.filter(k => k !== key);
        } else {
          this.expandedKeys = [...this.expandedKeys, key];
          // Defer the editor mount until the expand finishes so TinyMCE's init doesn't stutter the animation.
          setTimeout(() => {
            if (this.isExpanded(section)) this.mount(key);
          }, EXPAND_TRANSITION_MS);
        }
      },
      // Reserve the editor's height while it's deferred so the row still expands to its final size.
      editorReservation(section) {
        if (this.isExpanded(section) && !this.mountedKeys.includes(section._key)) {
          return { minHeight: `${this.editorHeight}px` };
        }
        return {};
      },
      mount(key) {
        if (!this.mountedKeys.includes(key)) {
          this.mountedKeys = [...this.mountedKeys, key];
        }
      },
      expandAll() {
        const keys = this.sections.map(section => section._key);
        this.mountedKeys = [...new Set([...this.mountedKeys, ...keys])];
        this.expandedKeys = keys;
      },
      collapseAll() {
        this.expandedKeys = [];
      },
      // Forces expanded editors to remount so they re-read content changed externally (e.g. Replace Content).
      refreshEditors() {
        this.editorRefreshKey += 1;
      },
      // Close the dragged row on "choose" (before the drag image is captured) so it drags as a light bar.
      collapseDragged(event) {
        const dragged = this.sections[event.oldIndex];
        if (!dragged) return;
        this.expandedKeys = this.expandedKeys.filter(key => key !== dragged._key);
        this.mountedKeys = this.mountedKeys.filter(key => key !== dragged._key);
      },
      // Reordering can blank an open editor's iframe; rebuild the open ones to re-read their content.
      onDragEnd() {
        this.dragging = false;
        this.mountedKeys = this.mountedKeys.filter(key => this.expandedKeys.includes(key));
        this.refreshEditors();
      },
      addSection() {
        const section = {
          title: SECTION_DEFAULT_TITLE,
          content: "",
          enabled: true,
          rtbLocked: false,
          position: this.sections.length,
          _key: `new-${++this.localCounter}`,
          _titleAuto: true,
        };
        this.sections = [...this.sections, section];
        this.mount(section._key);
        this.expandedKeys = [...this.expandedKeys, section._key];
      },
      requestDelete(section) {
        this.deleteTargetKey = section._key;
      },
      confirmDelete() {
        this.sections = this.sections.filter(section => section._key !== this.deleteTargetKey);
        this.expandedKeys = this.expandedKeys.filter(key => key !== this.deleteTargetKey);
        this.mountedKeys = this.mountedKeys.filter(key => key !== this.deleteTargetKey);
        this.deleteTargetKey = null;
      },
      cancelDelete() {
        this.deleteTargetKey = null;
      },
      startEditTitle(section) {
        this.editingTitle = section.title || "";
        this.editingKey = section._key;
      },
      commitTitle(section) {
        if (this.editingKey !== section._key) return;

        const value = (this.editingTitle || "").trim();

        this.$set(section, "title", value || SECTION_DEFAULT_TITLE);
        this.$set(section, "_titleAuto", value === "");
        this.editingKey = null;
      },
      cancelTitle() {
        this.editingKey = null;
      },
      onContentUpdate(section, content) {
        this.$set(section, "content", content);

        if (section._titleAuto) {
          this.$set(section, "title", this.deriveTitle(content) || SECTION_DEFAULT_TITLE);
        }

        this.$emit("content-change");
      },
      deriveTitle(content) {
        const doc = new DOMParser().parseFromString(content || "", "text/html");
        // Drop Twig placeholders (e.g. {{ section.number }}, {{ shortcodes.geo(...) }}) so the title
        // reflects the actual copy, not the inserted token.
        const stripTokens = raw => (raw || "").replace(/\{\{[\s\S]*?\}\}/g, " ");

        // Use the first block element that has real text (usually the heading), so the whole first
        // line becomes the title instead of just a couple of words.
        let raw = "";
        for (const el of doc.body.querySelectorAll("h1,h2,h3,h4,h5,h6,p,li")) {
          if (stripTokens(el.textContent).trim()) {
            raw = el.textContent;
            break;
          }
        }

        return (
          stripTokens(raw || doc.body.textContent)
            .replace(/\s+/g, " ")
            // Strip leading non-word characters (e.g. the "." left after the section number).
            .replace(/^[^\p{L}\p{N}]+/u, "")
            .trim()
            .slice(0, 120)
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  @import "../../@core/preset/preset/typography/index.scss";

  .additional-sections {
    margin-top: 24px;

    &__head {
      display: flex;
      align-items: flex-start;
      gap: 16px;
      margin-bottom: 16px;
    }

    &__intro {
      flex: 1 1 auto;
      min-width: 0;
    }

    &__title {
      @include heading-4();
      color: get-color("body");
      margin-bottom: 6px;
    }

    &__subtitle {
      @include text-caption();
      color: get-color("subtitle");
    }

    &__head-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 0 0 auto;
    }

    &__head-btn {
      height: 38px !important;
      padding: 0 16px !important;
      border-radius: 4px;
      text-transform: none;
      letter-spacing: normal;
      @include text-button-medium();

      &::v-deep .v-btn__content {
        @include text-button-medium();
      }
    }

    &__head-action {
      flex: 0 0 auto;
    }

    &__row {
      margin-bottom: 16px;

      &--ghost {
        opacity: 0.5;
      }
    }

    &__bar {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    &__lead {
      display: flex;
      align-items: center;
      gap: 9px;
      flex: 1 1 auto;
      min-width: 0;
    }

    &__handle {
      cursor: grab;
      color: get-color("icon") !important;
    }

    &__box {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      flex: 1 1 auto;
      min-width: 0;
      padding: 12px;
      background-color: get-color("element-bg-100");
      border: 1px solid get-color("border");
      border-radius: 4px;
      cursor: pointer;
      transition: border-color 0.2s ease;

      &:hover {
        border-color: get-color("primary-65");
      }
    }

    &__box-main {
      display: flex;
      align-items: center;
      gap: 16px;
      flex: 1 1 auto;
      min-width: 0;
    }

    &__badge {
      @include text-body-2-medium();
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      min-width: 36px;
      padding: 4px 8px;
      border-radius: 4px;
      background-color: get-color("primary-20");
      color: get-color("body");
      text-align: center;

      &--disabled {
        background-color: get-color("border");
        color: get-color("icon");
      }
    }

    &__divider {
      flex: 0 0 auto;
      width: 1px;
      height: 25px;
      background-color: get-color("border");
    }

    &__title-wrap {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1 1 auto;
      min-width: 0;
    }

    &__row-title {
      @include text-subtitle-1();
      font-weight: 500 !important;
      color: get-color("body");
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__box--disabled &__row-title {
      color: get-color("icon");
    }

    &__title-input {
      width: 100%;
      flex: 1 1 auto;
      margin: 0;
      padding: 0;
    }

    &__title-edit {
      cursor: pointer;
      color: get-color("icon") !important;
    }

    &__chevron {
      flex: 0 0 auto;
      color: get-color("body") !important;
      transition: transform 0.2s ease;
    }

    &__chevron--open {
      transform: rotate(180deg);
    }

    &__trail {
      display: flex;
      align-items: center;
      gap: 16px;
      flex: 0 0 auto;
    }

    &__toggle {
      display: inline-flex;
      align-items: center;
    }

    &__switch {
      margin: 0;
      padding: 0;

      &::v-deep {
        .v-input__slot {
          margin: 0;
        }

        // Vuetify reserves a right margin on the control for a label we don't render; drop it so the row stays symmetric.
        .v-input--selection-controls__input {
          margin-right: 0;
        }
      }
    }

    &__lock {
      cursor: pointer;
    }

    &__trash {
      cursor: pointer;
      color: get-color("body") !important;
    }

    &__editor {
      // Padding (not margin) so v-expand-transition animates the full height smoothly without a jump.
      padding-top: 16px;

      // Inert while dragging so a row can't be dropped into the editor content.
      &--dragging {
        pointer-events: none;
      }
    }

    &__add {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      width: 100%;
      padding: 8px;
      border: 1px dashed get-color("primary-65");
      border-radius: 6px;
      background-color: get-color("primary-10-hover");
      color: get-color("primary");
      cursor: pointer;
      @include text-button-medium();
    }
  }
</style>
